/**
 * Router-compat shim — bridges @/lib/router-compat v6 call sites to
 * @tanstack/react-router without hand-rewriting every component.
 * This is the same load-bearing pattern used in Klar's dev-copy migration.
 */
import {
  useNavigate as tsNavigate,
  useLocation as tsLocation,
  useParams as tsParams,
  useRouter,
  Link as TSLink,
  Navigate as TSNavigate,
  Outlet as TSOutlet,
} from "@tanstack/react-router";
import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  forwardRef,
  type ComponentProps,
  type ReactNode,
} from "react";

// ---------- shared URL parsing ----------

function parseTo(to: string): { pathname: string; search?: Record<string, string> | undefined; hash?: string | undefined } {
  const [beforeHash, hashStr] = (to ?? "").split("#");
  const [pathname, searchStr] = (beforeHash ?? "").split("?");
  return {
    // react-router keeps the current path for search-only ("?a=1") and
    // hash-only ("#section") targets; TanStack's "." means current route.
    pathname: pathname || ".",
    search: searchStr ? Object.fromEntries(new URLSearchParams(searchStr)) : undefined,
    hash: hashStr || undefined,
  };
}

// ---------- useNavigate ----------

type NavigateOptions = { replace?: boolean; state?: unknown };

type NavigateFn = {
  (to: string | number, options?: NavigateOptions): void;
  (delta: number): void;
};

export function useNavigate(): NavigateFn {
  const tsNav = tsNavigate();
  const router = useRouter();
  return useCallback((to: string | number, options?: NavigateOptions) => {
    if (typeof to === "number") {
      router.history.go(to);
      return;
    }
    const { pathname, search, hash } = parseTo(to);
    tsNav({
      to: pathname,
      search: search as never,
      ...(hash !== undefined ? { hash } : {}),
      state: options?.state as never,
      ...(options?.replace !== undefined ? { replace: options.replace } : {}),
    });
  }, [tsNav, router]) as NavigateFn;
}

// ---------- useLocation ----------

export function useLocation() {
  const loc = tsLocation();
  return useMemo(
    () => ({
      pathname: loc.pathname,
      search: loc.searchStr ? `?${loc.searchStr}` : "",
      hash: loc.hash ?? "",
      state: (loc.state ?? null) as unknown,
      key: loc.pathname + (loc.searchStr ?? ""),
    }),
    [loc.pathname, loc.searchStr, loc.hash, loc.state],
  );
}

// ---------- useParams ----------

export function useParams<T extends Record<string, string | undefined> = Record<string, string | undefined>>(): T {
  return tsParams({ strict: false } as never) as T;
}


// ---------- useNavigationType (react-router-dom compat) ----------

type NavType = "POP" | "PUSH" | "REPLACE";
const navTypeState = { current: "POP" as NavType, subscribedHistory: undefined as unknown };

/**
 * Mirrors react-router's useNavigationType: PUSH/REPLACE for programmatic
 * navs, POP for back/forward. Backed by @tanstack/history's action events.
 */
export function useNavigationType(): NavType {
  const router = useRouter();
  const loc = tsLocation();
  if (navTypeState.subscribedHistory !== router.history) {
    navTypeState.subscribedHistory = router.history;
    router.history.subscribe((ev: { action?: { type?: string } }) => {
      const t = ev?.action?.type;
      navTypeState.current = t === "PUSH" ? "PUSH" : t === "REPLACE" ? "REPLACE" : "POP";
    });
  }
  // Re-read on every location change so effects keyed on pathname see the
  // action that produced the current location.
  return useMemo(() => navTypeState.current, [loc.pathname, loc.searchStr, loc.hash]);
}

// ---------- useSearchParams (react-router-dom compat) ----------

export function useSearchParams(): [URLSearchParams, (init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams), opts?: { replace?: boolean }) => void] {
  const loc = tsLocation();
  const nav = tsNavigate();
  const router = useRouter();
  const params = useMemo(() => new URLSearchParams(loc.searchStr ?? ""), [loc.searchStr]);
  const setParams = useCallback(
    (
      init: URLSearchParams | Record<string, string> | ((prev: URLSearchParams) => URLSearchParams),
      opts?: { replace?: boolean },
    ) => {
      // Functional updaters read the router's live location, not the render
      // snapshot — react-router passes call-time params, and chained updates
      // within one tick must see each other's writes.
      const live = router.state.location;
      const current = new URLSearchParams(live.searchStr ?? "");
      const next =
        typeof init === "function"
          ? init(current)
          : init instanceof URLSearchParams
            ? init
            : new URLSearchParams(init);
      const searchObj: Record<string, string> = {};
      next.forEach((v, k) => { searchObj[k] = v; });
      nav({ to: live.pathname, search: searchObj as never, resetScroll: false, ...(opts?.replace !== undefined ? { replace: opts?.replace } : {}) });
    },
    [nav, router],
  );
  return [params, setParams];
}

// ---------- Link ----------

type LinkProps = Omit<ComponentProps<typeof TSLink>, "to"> & {
  to: string;
  replace?: boolean;
  state?: unknown;
  children?: ReactNode;
};

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { to, replace, state, children, ...rest },
  ref,
) {
  const { pathname, search, hash } = parseTo(to);
  return (
    <TSLink
      ref={ref as never}
      to={pathname as never}
      search={search as never}
      {...(hash !== undefined ? { hash } : {})}
      {...(replace !== undefined ? { replace } : {})}
      state={state as never}
      {...((rest ?? {}) as Record<string, unknown>)}
    >
      {children}
    </TSLink>
  );
});


// ---------- Navigate ----------

export function Navigate({ to, replace, state }: { to: string; replace?: boolean; state?: unknown }) {
  const { pathname, search, hash } = parseTo(to);
  return (
    <TSNavigate
      to={pathname as never}
      search={search as never}
      {...(hash !== undefined ? { hash } : {})}
      state={state as never}
      {...(replace !== undefined ? { replace } : {})}
    />
  );
}

// ---------- Outlet + useOutletContext (@/lib/router-compat compat) ----------

const OutletCtx = createContext<unknown>(undefined);

/**
 * react-router's <Outlet context={...}> / useOutletContext() pattern, bridged
 * over TanStack's Outlet via a plain React context. Used by AdminLayout and
 * CountryLayout to pass auth/topology state down to nested pages.
 */
export function Outlet({ context }: { context?: unknown } = {}) {
  if (context === undefined) return <TSOutlet />;
  return (
    <OutletCtx.Provider value={context}>
      <TSOutlet />
    </OutletCtx.Provider>
  );
}

export function useOutletContext<T = unknown>(): T {
  return useContext(OutletCtx) as T;
}

// ---------- NavLink (minimal) ----------

export const NavLink = Link;
